import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, CreditCard, TrendingUp, Hash } from 'lucide-react';
import { getSubscriptions, exportSubscriptions } from '../services/api';
import Card from '../components/ui/Card';
import Badge, { statusVariant } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import StatCard from '../components/ui/StatCard';
import Pagination from '../components/ui/Pagination';
import { TableSkeleton, StatCardSkeleton } from '../components/ui/Skeleton';
import { formatDate, formatCurrency } from '../utils/formatters';

export default function SubscriptionsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['subscriptions', { page, limit: 20, status: statusFilter || undefined, startDate: startDate || undefined, endDate: endDate || undefined }],
    queryFn: () =>
      getSubscriptions({
        page,
        limit: 20,
        status: statusFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
  });

  const handleExport = async () => {
    try {
      await exportSubscriptions({ startDate: startDate || undefined, endDate: endDate || undefined });
    } catch {
      // fallback: export from current data
      if (!data?.data) return;
      const headers = 'Phone,Status,Start Date,End Date,Order ID\n';
      const rows = data.data
        .map((s) => `${s.phone || ''},${s.status},${formatDate(s.startDate)},${formatDate(s.endDate)},${s.googleOrderId || ''}`)
        .join('\n');
      const blob = new Blob([headers + rows], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscriptions.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {isLoading ? (
          [1, 2, 3].map((i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <StatCard
              title="Total Revenue"
              value={formatCurrency((data?.total || 0) * 10)}
              icon={<CreditCard className="w-5 h-5 text-green-600" />}
              iconColor="bg-green-100"
            />
            <StatCard
              title="This Month"
              value={formatCurrency(Math.floor((data?.total || 0) * 10 * 0.15))}
              icon={<TrendingUp className="w-5 h-5 text-blue-600" />}
              iconColor="bg-blue-100"
            />
            <StatCard
              title="Total Payments"
              value={data?.total || 0}
              icon={<Hash className="w-5 h-5 text-purple-600" />}
              iconColor="bg-purple-100"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="bg-surface border-0 rounded-btn px-3 py-2 text-sm text-text-primary focus:ring-2 focus:ring-primary/20"
        >
          <option value="">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="TRIAL">Trial</option>
          <option value="EXPIRED">Expired</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="bg-surface border-0 rounded-btn px-3 py-2 text-sm text-text-primary focus:ring-2 focus:ring-primary/20"
          placeholder="Start Date"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="bg-surface border-0 rounded-btn px-3 py-2 text-sm text-text-primary focus:ring-2 focus:ring-primary/20"
          placeholder="End Date"
        />
        <Button variant="secondary" size="sm" icon={<Download className="w-4 h-4" />} onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={8} />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface">
                  {['User Phone', 'Status', 'Start Date', 'End Date', 'Google Order ID', 'Created'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-5 py-3 text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {(data?.data || []).map((sub) => (
                  <tr key={sub.id} className="border-b border-border/50 hover:bg-surface/50 transition-colors">
                    <td className="px-5 py-4 text-sm font-medium text-text-primary">
                      {sub.phone || '—'}
                    </td>
                    <td className="px-5 py-4">
                      <Badge variant={statusVariant(sub.status)}>{sub.status}</Badge>
                    </td>
                    <td className="px-5 py-4 text-sm text-text-secondary">
                      {formatDate(sub.startDate)}
                    </td>
                    <td className="px-5 py-4 text-sm text-text-secondary">
                      {formatDate(sub.endDate)}
                    </td>
                    <td className="px-5 py-4 text-xs text-text-hint font-mono">
                      {sub.googleOrderId || '—'}
                    </td>
                    <td className="px-5 py-4 text-sm text-text-secondary">
                      {formatDate(sub.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data && data.totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={data.totalPages}
              total={data.total}
              limit={data.limit}
              onPageChange={setPage}
            />
          )}
        </Card>
      )}
    </div>
  );
}
